import React from "react"
import { useTranslation } from "react-i18next"
import { useId } from "@reach/auto-id"

import { handleInternalInputValidation, handlePreventTabsValueAtEndOnInput } from "../formUtils"
import { Errors } from "../Errors"
import { isNotEmpty } from "utils/validation"

import { EyeSeeIcon, EyeHideIcon } from "components/Elements/Icons"

export const InputPassword = ({
    // Attributes
    id,
    value,
    name,
    label = "",
    placeholder = "",
    disabled,
    readonly = false,
    autocomplete = "current-password",
    // Styles
    style,
    className = "",
    classNameLabel = "",
    classNameInput = "",
    // Error management
    passwordToConfirm = null,
    passwordCreate = false,
    required = false,
    customRules,
    showErrors,
    // Handler
    onChange,
    onValidate
}) => {
    const [errors, setErrors] = React.useState([])
    const [passwordVisible, setPasswordVisible] = React.useState(false)
    const [showpreventPasteError, setShowPreventPasteError] = React.useState(false)
    const passwordType = passwordVisible ? "text" : "password"
    const { t } = useTranslation()

    const prefix = useId()
    const inputId = id ?? `${prefix}-${name}`
    const errorId = `${inputId}-error`

    const rules = React.useMemo(() => [
        {
            key: "password-empty",
            check: (value) => required ? isNotEmpty(value) : true,
            message: t("password_error_mandatory", { ns: "form" })
        },
        {
            key: "password-not-confirmed",
            check: (value) => (isNotEmpty(value) && passwordToConfirm) ? (passwordToConfirm === value) : true,
            message: t("password_error_confirmation", { ns: "form" })
        },
        // {
        //     key: "password-invalid",
        //     check: (value) => (required || (isNotEmpty(value) && passwordCreate)) ? isPasswordValid(value) : true,
        //     message: t("password_error_invalid", { ns: "form" })
        // },
        // Temp
        {
            key: "password-invalid",
            check: (value) => (required || (isNotEmpty(value) && passwordCreate)) ? value.length >= 8 : true,
            message: t("password_error_invalid", { ns: "form" })
        },
        ...(customRules || [])
    ], [required, passwordToConfirm, passwordCreate, t]) // eslint-disable-line
    // customRules in dependencies array trigger an infinite loop,
    // because custom rules include a check property defining a new function,
    // preventing useMemo to return a valid shallow-comparison
    // or event useDeepMemo to return a valid deep-comparison

    const validate = React.useCallback(
        (currentValue) =>
            handleInternalInputValidation(currentValue, name, rules, setErrors, onValidate),
        [rules, name, onValidate]
    )

    // validate on mount and each time value change
    React.useEffect(() => {
        validate(value)
        return () => {
            // On unmounted erros and validation are no longer needed
            setErrors([])
            if (onValidate) onValidate(name, [])
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, passwordToConfirm, validate])

    return (
        <>
            <div data-cy={`formfield-${name}`} className={"c-form-group " + className} style={style}>
                <label data-cy={`label-${name}`} className={"c-label mb-1 " + classNameLabel} htmlFor={inputId}>{label} {required && "*"}</label>
                <input
                    data-cy={`input-${name}`}
                    className={
                        "c-input "
                        + (showErrors && errors.length > 0 ? "has-error " : " ")
                        + (classNameInput ? classNameInput : "")
                    }
                    id={inputId}
                    name={name}
                    type={passwordType}
                    placeholder={placeholder}
                    value={value}
                    disabled={disabled}
                    autoComplete={
                        passwordCreate
                            ? "new-password"
                            : passwordToConfirm !== null
                            ? "off"
                            : autocomplete
                    }
                    required={required}
                    readOnly={readonly}
                    onChange={(e) => !readonly && onChange(handlePreventTabsValueAtEndOnInput(e))}
                    onBlur={() => validate(value)}
                    onPaste={(e) => {
                        if (passwordToConfirm !== null) {
                            e.preventDefault()
                            setShowPreventPasteError(true)
                            setTimeout(() => setShowPreventPasteError(false), 2000)
                        }
                    }}
                    aria-describedby={errorId}
                    aria-invalid={Boolean(errors.length)}
                />

                { value.length > 0 &&
                    <button type="button" onClick={() => setPasswordVisible(!passwordVisible)} aria-label={t("password_visibility_alt", { ns: "form" })}>
                    {passwordVisible ? <EyeSeeIcon size={25} /> : <EyeHideIcon size={25} />}
                    </button>
                }

                {/* Error messages */}
				{showErrors && <Errors dataCy={`error-${name}`} id={errorId} errors={errors} />}
                {showpreventPasteError &&
                    <p className="text-xs text-danger">{t("prevent_pasting", { ns: "form" })}</p>
                }
            </div>
        </>
    )
}
